import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    Sparkles,
    ChevronRight,
    Brain,
    Database,
    Shield,
    Code,
    Monitor,
    X,
    Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { captureBrochureLead } from "../lib/api";
import {
    CS_PROGRAMME_HERO,
    CS_PROGRAMME_YEARS,
    CS_PROGRAMME_YEARS_SECTION,
    CS_PROGRAMME_STATS,
    CS_PROGRAMME_OFFERINGS,
    CS_VEDA_SECTION,
    CS_PLACEMENT_SECTION,
    CS_CMU_SECTION,
    CS_ELIGIBILITY_SECTION,
    CS_CTA,
    DOMAIN_IMAGES,
} from "../data/programmes";
import { CS_FAQS } from "../data/faq-data";
import { CS_SPECIALIZATIONS } from "../data/home-data";
import FAQ from "../components/FAQ";

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.5, ease: [0.23, 1, 0.32, 1] },
};

const stagger = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
};

const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            delay: i * 0.08,
            duration: 0.5,
            ease: [0.23, 1, 0.32, 1],
        },
    }),
    hover: {
        y: -8,
        scale: 1.02,
        transition: { duration: 0.3, ease: "easeOut" },
    },
};

const domainImages = DOMAIN_IMAGES;

const domainIcons = {
    "Artificial Intelligence & Machine Learning": Brain,
    "Data Science": Database,
    "Cyber Security": Shield,
    "Computer Science & Engineering": Code,
    "Information Technology": Monitor,
};

export default function CSProgrammePage() {
    const navigate = useNavigate();
    const [isMarqueePaused, setIsMarqueePaused] = useState(false);
    const [brochureModal, setBrochureModal] = useState(false);
    const [brochureForm, setBrochureForm] = useState({ name: "", email: "", phone: "" });
    const [brochureLoading, setBrochureLoading] = useState(false);
    const [brochureError, setBrochureError] = useState("");

    async function handleBrochureSubmit(e) {
        e.preventDefault();
        const email = brochureForm.email.trim();
        const phone = brochureForm.phone.trim();
        if (!email && !phone) {
            setBrochureError("Please enter at least your email or phone number.");
            return;
        }
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setBrochureError("Please enter a valid email address.");
            return;
        }
        setBrochureLoading(true);
        setBrochureError("");
        try {
            await captureBrochureLead({ name: brochureForm.name.trim(), email: email || undefined, phone: phone || undefined });
            // Trigger download then close
            const link = document.createElement("a");
            link.href = `${process.env.PUBLIC_URL}/general/ai.prof_brochure.pdf`;
            link.download = "AI.Prof_Brochure.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setBrochureModal(false);
            setBrochureForm({ name: "", email: "", phone: "" });
            toast.success("Download started! Check your downloads folder.");
        } catch (err) {
            setBrochureError("Something went wrong. Please try again.");
        } finally {
            setBrochureLoading(false);
        }
    }

    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <main className="pt-20" data-testid="cs-programme-page">
            {/* HERO */}
            <section className="relative overflow-hidden text-white px-6 sm:px-12 pt-32 pb-20 sm:py-24 bg-gradient-to-br from-gray-900/90 via-black/80 to-gray-900/90">
                <div className="absolute inset-0 z-0">
                    <img
                        src={CS_PROGRAMME_HERO.heroImage}
                        alt="Computer Science"
                        className="w-full h-full object-cover"
                        loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-transparent" />
                </div>
                <motion.div
                    {...fadeUp}
                    className="relative z-10 max-w-7xl mx-auto"
                >
                    <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-aiorange text-xs font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full mb-5">
                        <span className="w-1.5 h-1.5 bg-aiorange rounded-full" />{" "}
                        {CS_PROGRAMME_HERO.badge}
                    </span>
                    <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl tracking-normal leading-[1.08] max-w-3xl">
                        {CS_PROGRAMME_HERO.headline}
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-white/60 max-w-2xl font-light leading-relaxed">
                        {CS_PROGRAMME_HERO.description}
                    </p>

                    <div className="mt-8 flex gap-3 flex-wrap">
                        <Button
                            onClick={() => navigate("/btech-enroll")}
                            className="bg-aiorange hover:bg-orange-600 text-black font-semibold rounded-lg px-7 py-3"
                        >
                            {CS_PROGRAMME_HERO.ctaPrimary}{" "}
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                </motion.div>
            </section>

            {/* STATS BAR */}
            <div className="bg-white border-b border-gray-100 px-6 sm:px-12">
                <div className="max-w-7xl mx-auto flex flex-wrap">
                    {CS_PROGRAMME_STATS.map((s, i) => (
                        <motion.div
                            key={s.label}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.06 }}
                            className="flex-1 min-w-[130px] px-5 py-6 border-r border-gray-100 last:border-r-0"
                        >
                            <span className="font-display font-bold text-3xl text-black block leading-none">
                                {s.num}
                            </span>
                            <span className="text-xs text-gray-400 font-medium mt-1 block tracking-wide">
                                {s.label}
                            </span>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* PROGRAMS */}
            <section
                className="pt-20 px-6 sm:px-12 bg-white overflow-hidden"
                id="programs"
            >
                <motion.div {...fadeUp} className="max-w-7xl mx-auto">
                    <p className="text-xs uppercase tracking-[0.3em] text-aiblue font-bold mb-3">
                        {CS_PROGRAMME_OFFERINGS.tag}
                    </p>
                    <h2
                        className="font-display font-black text-3xl sm:text-4xl tracking-normal max-w-3xl"
                        dangerouslySetInnerHTML={{
                            __html: CS_PROGRAMME_OFFERINGS.headline,
                        }}
                    />
                    <p className="mt-4 text-base text-gray-500 max-w-xl leading-relaxed mb-8">
                        {CS_PROGRAMME_OFFERINGS.subtext}
                    </p>
                </motion.div>
                <div
                    className="marquee-wrapper overflow-hidden"
                    onMouseEnter={() => setIsMarqueePaused(true)}
                    onMouseLeave={() => setIsMarqueePaused(false)}
                >
                    <div
                        className={`marquee-track flex gap-6 ${isMarqueePaused ? "paused" : ""}`}
                    >
                        {/* First copy */}
                        {CS_SPECIALIZATIONS.map((spec) => {
                            const Icon = domainIcons[spec.name];
                            return (
                                <div
                                    key={`a-${spec.name}`}
                                    className="group flex-shrink-0 w-[320px] rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow cursor-pointer"
                                >
                                    <div className="relative h-40 overflow-hidden">
                                        <img
                                            src={domainImages[spec.name]}
                                            alt={spec.name}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                        <div className="absolute bottom-3 left-4 right-4">
                                            <h4 className="font-display font-bold text-sm text-white leading-tight drop-shadow-lg">
                                                {spec.name}
                                            </h4>
                                        </div>
                                        {spec.featured && (
                                            <span className="absolute top-3 right-3 bg-aiorange text-black text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-md shadow-lg">
                                                {
                                                    CS_PROGRAMME_OFFERINGS.badgeMostPopular
                                                }
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-5 bg-white">
                                        <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">
                                            {spec.about || spec.desc}
                                        </p>
                                        <button
                                            onClick={() =>
                                                navigate("/btech-enroll")
                                            }
                                            className="text-xs font-semibold text-aiblue hover:text-aiorange flex items-center gap-1 group/btn transition-colors"
                                        >
                                            {CS_PROGRAMME_OFFERINGS.cta}{" "}
                                            <ArrowRight className="h-3 w-3 group-hover/btn:translate-x-1 transition-transform" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        {/* Second copy — for seamless loop */}
                        {CS_SPECIALIZATIONS.map((spec) => {
                            const Icon = domainIcons[spec.name];
                            return (
                                <div
                                    key={`b-${spec.name}`}
                                    className="group flex-shrink-0 w-[320px] rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow cursor-pointer"
                                >
                                    <div className="relative h-40 overflow-hidden">
                                        <img
                                            src={domainImages[spec.name]}
                                            alt={spec.name}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                        <div className="absolute bottom-3 left-4 right-4">
                                            <h4 className="font-display font-bold text-sm text-white leading-tight drop-shadow-lg">
                                                {spec.name}
                                            </h4>
                                        </div>
                                        {spec.featured && (
                                            <span className="absolute top-3 right-3 bg-aiorange text-black text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-md shadow-lg">
                                                {
                                                    CS_PROGRAMME_OFFERINGS.badgeMostPopular
                                                }
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-5 bg-white">
                                        <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">
                                            {spec.about || spec.desc}
                                        </p>
                                        <button
                                            onClick={() =>
                                                navigate("/btech-enroll")
                                            }
                                            className="text-xs font-semibold text-aiblue hover:text-aiorange flex items-center gap-1 group/btn transition-colors"
                                        >
                                            {CS_PROGRAMME_OFFERINGS.cta}{" "}
                                            <ArrowRight className="h-3 w-3 group-hover/btn:translate-x-1 transition-transform" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Marquee Animation Styles */}
                <style>{`
        .marquee-wrapper {
          width: 100%;
          overflow: hidden;
        }
        .marquee-track {
          display: flex;
          width: max-content;
          animation: cs-scroll-left 60s linear infinite;
        }
        .marquee-track.paused {
          animation-play-state: paused;
        }
        @keyframes cs-scroll-left {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
            </section>

            {/* 4-YEAR JOURNEY — Diagonal Stepped Layout */}
            <section
                className="py-20 px-6 sm:px-12 bg-gradient-to-br from-gray-50/80 via-white to-gray-50/80 overflow-hidden"
                id="journey"
            >
                <motion.div
                    {...fadeUp}
                    className="max-w-7xl mx-auto text-center mb-14"
                >
                    <p className="text-xs uppercase tracking-[0.3em] text-aiblue font-bold mb-3">
                        {CS_PROGRAMME_YEARS_SECTION.tag}
                    </p>
                    <h2 className="font-display font-black text-4xl sm:text-5xl tracking-normal max-w-3xl mx-auto">
                        {
                            CS_PROGRAMME_YEARS_SECTION.headline.split(
                                "what happens each year",
                            )[0]
                        }
                        <span className="text-aiblue">
                            what happens each year
                        </span>
                    </h2>
                    <p className="mt-4 text-base text-gray-500 max-w-2xl mx-auto leading-relaxed">
                        {CS_PROGRAMME_YEARS_SECTION.description}
                    </p>
                </motion.div>

                <div className="max-w-6xl mx-auto relative">
                    {/* Connecting line */}
                    <div className="absolute left-[24px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-aiblue via-aiorange to-aiblue md:left-1/2 md:-translate-x-px" />

                    <div className="flex flex-col gap-16">
                        {CS_PROGRAMME_YEARS.map((y, i) => {
                            const isLeft = i % 2 === 0;
                            return (
                                <motion.div
                                    key={y.n}
                                    initial={{
                                        opacity: 0,
                                        x: isLeft ? -40 : 40,
                                    }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{
                                        duration: 0.5,
                                        delay: i * 0.1,
                                    }}
                                    className={`relative flex flex-col md:flex-row items-center gap-6 ${isLeft ? "md:flex-row" : "md:flex-row-reverse"}`}
                                >
                                    {/* Step marker on the line */}
                                    <div className="absolute left-[24px] md:left-1/2 md:-translate-x-1/2 top-0 w-6 h-6 rounded-full bg-aiblue border-4 border-white shadow-md z-10" />

                                    {/* Content card */}
                                    <div
                                        className={`flex-1 bg-white rounded-2xl border overflow-hidden shadow-sm hover:shadow-lg transition-all ${
                                            y.accentColor === "blue"
                                                ? "border-aiblue/20"
                                                : "border-aiorange/20"
                                        }`}
                                    >
                                        <div className="flex flex-col sm:flex-row">
                                            {/* Image */}
                                            <div className="sm:w-52 shrink-0 h-44 sm:h-auto relative overflow-hidden">
                                                <img
                                                    src={y.image}
                                                    alt={y.title}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                                                <span className="absolute bottom-3 left-4 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                                                    {y.title}
                                                </span>
                                            </div>

                                            {/* Text */}
                                            <div className="flex-1 p-5 sm:p-6">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <span
                                                        className={`w-8 h-8 rounded-full ${
                                                            y.accentColor ===
                                                            "blue"
                                                                ? "bg-aiblue"
                                                                : "bg-aiorange"
                                                        } flex items-center justify-center text-white font-display font-bold text-sm shadow-md`}
                                                    >
                                                        {y.n}
                                                    </span>
                                                    <span className="font-display font-black text-lg text-black whitespace-nowrap">
                                                        {y.title}
                                                    </span>
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 whitespace-nowrap">
                                                        · {y.tagline}
                                                    </span>
                                                </div>
                                                <ul className="space-y-2.5">
                                                    {y.bullets.map((b, bi) => (
                                                        <li
                                                            key={bi}
                                                            className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed"
                                                        >
                                                            <span
                                                                className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                                                                    y.accentColor ===
                                                                    "blue"
                                                                        ? "bg-aiblue"
                                                                        : "bg-aiorange"
                                                                }`}
                                                            />
                                                            <span
                                                                dangerouslySetInnerHTML={{
                                                                    __html: b,
                                                                }}
                                                            />
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Empty spacer for alignment */}
                                    <div className="flex-1 hidden md:block" />
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* VEDA */}
            <section className="relative overflow-hidden py-20 px-6 sm:px-12 bg-gradient-to-br from-aiorange via-orange-500 to-amber-500 text-white">
                <div className="absolute top-[-80px] right-[-80px] w-[400px] h-[400px] rounded-full bg-white/10 pointer-events-none" />
                <div className="absolute bottom-[-60px] left-[5%] w-[300px] h-[300px] rounded-full bg-amber-300/15 pointer-events-none" />
                <div className="absolute top-[40%] right-[20%] w-[200px] h-[200px] rounded-full bg-white/5 pointer-events-none" />
                <motion.div
                    {...fadeUp}
                    className="relative z-10 max-w-7xl mx-auto"
                >
                    <p className="text-xs uppercase tracking-[0.3em] text-white/80 font-bold mb-3">
                        {CS_VEDA_SECTION.tag}
                    </p>
                    <h2 className="font-display font-black text-4xl sm:text-5xl tracking-normal max-w-3xl">
                        {CS_VEDA_SECTION.headline.split("—")[0]}—{" "}
                        <span className="text-black font-black">
                            {CS_VEDA_SECTION.headline
                                .split("—")
                                .slice(1)
                                .join("—")}
                        </span>
                    </h2>
                    <p className="mt-4 text-base text-white/80 max-w-2xl leading-relaxed">
                        {CS_VEDA_SECTION.description}
                    </p>
                </motion.div>
                <div className="relative z-10 max-w-7xl mx-auto mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {CS_VEDA_SECTION.features.map((v, i) => (
                        <motion.div
                            key={v.title}
                            initial={{ opacity: 0, y: 30, scale: 0.9 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{
                                delay: i * 0.1,
                                duration: 0.5,
                                ease: [0.23, 1, 0.32, 1],
                            }}
                            whileHover={{
                                y: -8,
                                scale: 1.03,
                                backgroundColor: "rgba(255,255,255,0.25)",
                            }}
                            className={`${v.color} backdrop-blur-sm border border-white/20 rounded-2xl p-7 transition-all`}
                        >
                            <motion.div
                                initial={{ scale: 0 }}
                                whileInView={{ scale: 1 }}
                                viewport={{ once: true }}
                                transition={{
                                    delay: i * 0.1 + 0.2,
                                    type: "spring",
                                    stiffness: 200,
                                }}
                                className="text-3xl mb-3.5"
                            >
                                {v.icon}
                            </motion.div>
                            <h4 className="font-display font-bold text-base text-white mb-2">
                                {v.title}
                            </h4>
                            <p className="text-sm text-white/70 leading-relaxed">
                                {v.desc}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* PLACEMENT */}
            <section
                className="py-20 px-6 sm:px-12 relative overflow-hidden bg-gradient-to-b from-[#FFF5F0] via-white to-white"
                id="placement"
            >
                {/* Decorative blobs */}
                <div className="absolute top-40 -right-32 w-[500px] h-[500px] rounded-full blur-3xl opacity-20 bg-gradient-to-bl from-aiorange to-aipeach pointer-events-none" />
                <div className="absolute bottom-40 -left-32 w-[400px] h-[400px] rounded-full blur-3xl opacity-15 bg-gradient-to-tr from-aiblue to-aisky pointer-events-none" />

                <motion.div
                    {...fadeUp}
                    className="max-w-7xl mx-auto relative z-10 text-center"
                >
                    <p className="text-xs uppercase tracking-[0.3em] text-aiorange font-bold mb-3">
                        {CS_PLACEMENT_SECTION.tag}
                    </p>
                    <h2
                        className="font-display font-black text-4xl sm:text-5xl tracking-normal max-w-3xl mx-auto"
                        dangerouslySetInnerHTML={{
                            __html: CS_PLACEMENT_SECTION.headline,
                        }}
                    />
                    <p className="mt-4 text-base text-gray-500 max-w-2xl mx-auto leading-relaxed">
                        {CS_PLACEMENT_SECTION.description}
                    </p>
                </motion.div>

                {/* Metrics — compact badges */}
                <div className="max-w-3xl mx-auto mt-10 relative z-10 flex flex-wrap justify-center gap-4">
                    {CS_PLACEMENT_SECTION.metrics.map((m, i) => (
                        <motion.div
                            key={m.desc}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.08 }}
                            whileHover={{ y: -3 }}
                            className="bg-white rounded-full border border-aiorange/20 px-6 py-3 shadow-sm hover:shadow-md transition-all flex items-center gap-3"
                        >
                            <span className="font-display font-black text-2xl text-aiblue">
                                {m.num}
                            </span>
                            <span className="text-sm text-gray-500 font-medium">
                                {m.desc}
                            </span>
                        </motion.div>
                    ))}
                </div>

                {/* Timeline Content — 2x2 grid cards */}
                <div className="max-w-4xl mx-auto mt-6 relative z-10 grid sm:grid-cols-2 gap-4">
                    {CS_PLACEMENT_SECTION.timeline.map((t, i) => (
                        <motion.div
                            key={t.dot}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 + i * 0.08 }}
                            whileHover={{ y: -3 }}
                            className="bg-white rounded-2xl border border-aiorange/15 p-5 shadow-sm hover:shadow-md transition-all"
                        >
                            <span className="text-xs font-bold uppercase tracking-widest text-aiblue mb-2 block">
                                {t.dot}
                            </span>
                            <h4 className="font-display font-bold text-base text-black mb-1.5">
                                {t.title}
                            </h4>
                            <p className="text-sm text-gray-500 leading-relaxed">
                                {t.desc}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CMU / GLOBAL */}
            <section
                className="py-20 px-6 sm:px-12 bg-gradient-to-b from-amber-50/50 via-white to-[#FFF5F0]/50 border-t border-aiorange/10"
                id="global"
            >
                <motion.div
                    {...fadeUp}
                    className="max-w-7xl mx-auto text-center"
                >
                    <p className="text-xs uppercase tracking-[0.3em] text-aiblue font-bold mb-3">
                        {CS_CMU_SECTION.tag}
                    </p>
                    <h2 className="font-display font-black text-4xl sm:text-6xl tracking-normal max-w-3xl mx-auto leading-tight">
                        {CS_CMU_SECTION.headline.split("Builders")[0]}{" "}
                        <span className="text-aiblue">Builders.</span>
                    </h2>
                    <p className="mt-3 text-base text-gray-500 max-w-xl mx-auto leading-relaxed">
                        {CS_CMU_SECTION.description}
                    </p>
                </motion.div>

                <div className="max-w-7xl mx-auto mt-12 grid md:grid-cols-3 gap-6">
                    {CS_CMU_SECTION.cards.map((c, i) => (
                        <motion.div
                            key={c.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.08 }}
                            whileHover={{ y: -6 }}
                            className="group bg-white border border-aiorange/20 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all"
                        >
                            {/* Image with accent overlay */}
                            <div className="relative h-44 overflow-hidden">
                                <img
                                    src={c.img}
                                    alt={c.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                                {/* Badges */}
                                <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
                                    <span
                                        className={`w-8 h-8 rounded-full ${c.accent} flex items-center justify-center text-white font-display font-bold text-sm shadow-lg`}
                                    >
                                        {c.num}
                                    </span>
                                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/90 bg-black/30 backdrop-blur-sm px-2.5 py-1 rounded-full">
                                        {c.tag}
                                    </span>
                                </div>
                            </div>
                            {/* Content */}
                            <div className="p-6">
                                <h4 className="font-display font-bold text-base text-black mb-2.5">
                                    {c.title}
                                </h4>
                                <p className="text-sm text-gray-500 leading-relaxed">
                                    {c.desc}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ELIGIBILITY */}
            <section
                className="py-20 px-6 sm:px-12 bg-gradient-to-b from-[#FFF5F0] via-white to-white"
                id="admissions"
            >
                <motion.div
                    {...fadeUp}
                    className="max-w-7xl mx-auto text-center"
                >
                    <p className="text-xs uppercase tracking-[0.3em] text-aiblue font-bold mb-3">
                        {CS_ELIGIBILITY_SECTION.tag}
                    </p>
                    <h2 className="font-display font-black text-4xl sm:text-5xl tracking-normal max-w-3xl mx-auto">
                        {CS_ELIGIBILITY_SECTION.headline.split("apply?")[0]}{" "}
                        <span className="text-aiblue">apply?</span>
                    </h2>
                    <p className="mt-4 text-base text-gray-500 max-w-xl mx-auto leading-relaxed">
                        {CS_ELIGIBILITY_SECTION.subtext}
                    </p>
                </motion.div>

                <div className="max-w-4xl mx-auto mt-10 grid md:grid-cols-2 gap-6">
                    {CS_ELIGIBILITY_SECTION.cards.map((e, i) => (
                        <motion.div
                            key={e.tag}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            whileHover={{ y: -4 }}
                            className={`bg-white rounded-2xl border overflow-hidden shadow-sm hover:shadow-xl transition-all ${
                                e.accent === "aiblue"
                                    ? "border-aiblue/20"
                                    : "border-aiorange/20"
                            }`}
                        >
                            <div className="p-6 sm:p-8">
                                {/* Icon + Tag */}
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="text-3xl">{e.icon}</span>
                                    <span
                                        className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full ${
                                            e.accent === "aiblue"
                                                ? "bg-aiblue/10 text-aiblue"
                                                : "bg-aiorange/10 text-aiorange"
                                        }`}
                                    >
                                        {e.tag}
                                    </span>
                                </div>

                                {/* Title + Desc */}
                                <h3 className="font-display font-black text-2xl text-black mb-3">
                                    {e.title}
                                </h3>
                                <p className="text-sm text-gray-500 leading-relaxed mb-5">
                                    {e.desc}
                                </p>

                                {/* Checklist */}
                                <ul className="space-y-2.5 mb-6">
                                    {e.checklist.map((item, ci) => (
                                        <li
                                            key={ci}
                                            className="flex items-start gap-2.5 text-sm text-gray-700"
                                        >
                                            <span
                                                className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-white text-xs font-bold ${
                                                    e.accent === "aiblue"
                                                        ? "bg-aiblue"
                                                        : "bg-aiorange"
                                                }`}
                                            >
                                                ✓
                                            </span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>

                                {/* CTA */}
                                <Button
                                    onClick={() =>
                                        navigate(
                                            e.accent === "aiblue"
                                                ? "/btech-enroll"
                                                : "/waitlist?role=university"
                                        )
                                    }
                                    className={`w-full rounded-full py-5 font-semibold text-sm ${
                                        e.accent === "aiblue"
                                            ? "bg-aiblue hover:bg-blue-700 text-white"
                                            : "bg-aiorange hover:bg-orange-600 text-black"
                                    }`}
                                >
                                    {e.cta}{" "}
                                    <ArrowRight className="ml-1.5 h-4 w-4" />
                                </Button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CTA FOOTER */}
            <section className="py-20 px-6 sm:px-12 bg-black text-white text-center">
                <motion.div {...fadeUp} className="max-w-3xl mx-auto">
                    <h2 className="font-display font-black text-4xl sm:text-5xl tracking-normal leading-tight">
                        {CS_CTA.headline1}
                        <br />
                        <span className="text-aiorange">
                            {CS_CTA.headline2}
                        </span>
                    </h2>
                    <p className="mt-4 text-base text-white/50 max-w-xl mx-auto leading-relaxed">
                        {CS_CTA.description}
                    </p>
                    <div className="mt-8 flex justify-center gap-3 flex-wrap">
                        <Button
                            onClick={() => navigate("/btech-enroll")}
                            className="bg-aiorange hover:bg-orange-600 text-black font-semibold rounded-lg px-8 py-3"
                        >
                            {CS_CTA.ctaPrimary}{" "}
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                        <button
                            onClick={() => setBrochureModal(true)}
                            className="border border-white/30 text-white hover:bg-white/10 rounded-lg px-8 py-3 font-semibold text-sm inline-flex items-center gap-2"
                        >
                            <Download className="h-4 w-4" />
                            {CS_CTA.ctaSecondary}
                        </button>
                    </div>
                </motion.div>
            </section>

            {/* FAQ */}
            <FAQ
                tag={CS_CTA.faqTag}
                headline={CS_CTA.faqHeadline}
                items={CS_FAQS}
                tabs={true}
            />

            {/* Brochure email gate modal */}
            <AnimatePresence>
                {brochureModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-black/60 backdrop-blur-sm overflow-y-auto"
                        onClick={() => setBrochureModal(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.93, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.93, y: 20 }}
                            transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 relative mx-auto my-auto"
                        >
                            {/* Close */}
                            <button
                                onClick={() => setBrochureModal(false)}
                                className="absolute top-5 right-5 text-gray-400 hover:text-gray-700 transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>

                            {/* Icon */}
                            <div className="flex justify-center mb-5">
                                <div className="h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center">
                                    <Download className="h-6 w-6 text-aiblue" />
                                </div>
                            </div>

                            <h3 className="font-display font-black text-2xl text-gray-900 text-center mb-1">
                                Get the Brochure
                            </h3>
                            <p className="text-sm text-gray-500 text-center mb-6">
                                Enter your details and the PDF will download instantly.
                            </p>

                            <form onSubmit={handleBrochureSubmit} className="space-y-4">
                                <div>
                                    <Label className="text-sm font-medium text-gray-700">
                                        Your Name
                                    </Label>
                                    <Input
                                        type="text"
                                        placeholder="Arjun Sharma"
                                        value={brochureForm.name}
                                        onChange={(e) => setBrochureForm((f) => ({ ...f, name: e.target.value }))}
                                        className="mt-1.5 h-12 rounded-xl"
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm font-medium text-gray-700">
                                        Email Address <span className="text-gray-400 font-normal">(optional)</span>
                                    </Label>
                                    <Input
                                        type="email"
                                        placeholder="arjun@example.com"
                                        value={brochureForm.email}
                                        onChange={(e) => {
                                            setBrochureForm((f) => ({ ...f, email: e.target.value }));
                                            setBrochureError("");
                                        }}
                                        className={`mt-1.5 h-12 rounded-xl ${brochureError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm font-medium text-gray-700">
                                        Phone Number
                                    </Label>
                                    <Input
                                        type="tel"
                                        placeholder="9876543210"
                                        value={brochureForm.phone}
                                        onChange={(e) => {
                                            setBrochureForm((f) => ({ ...f, phone: e.target.value }));
                                            setBrochureError("");
                                        }}
                                        className={`mt-1.5 h-12 rounded-xl ${brochureError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                    />
                                    {brochureError && (
                                        <p className="mt-1 text-xs text-red-500 font-medium">{brochureError}</p>
                                    )}
                                </div>

                                <Button
                                    type="submit"
                                    disabled={brochureLoading}
                                    className="w-full rounded-xl bg-aiblue hover:bg-blue-700 text-white font-semibold py-5 mt-2"
                                >
                                    {brochureLoading ? "Please wait…" : "Download Brochure"}
                                </Button>
                            </form>

                            <p className="text-center text-xs text-gray-400 mt-4">
                                No spam. We'll only send relevant programme updates.
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </main>
    );
}
